import React, { useState, useEffect, useRef } from 'react';
import api from '../../utils/api';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { 
  Building2, Save, Upload, Mail, Phone, MapPin, Globe, 
  Image, CheckCircle, AlertCircle, RefreshCcw, User, Trash2,
  Palette, FileText, Stamp
} from 'lucide-react';
import { toast } from 'sonner';

// Searchable country list (native datalist) for the "Pays" field
const COUNTRIES = [
  'France', 'Belgique', 'Suisse', 'Luxembourg', 'Monaco', 'Canada', 'Allemagne',
  'Espagne', 'Italie', 'Portugal', 'Royaume-Uni', 'Irlande', 'Pays-Bas', 'Autriche',
  'Danemark', 'Suède', 'Norvège', 'Finlande', 'Pologne', 'Grèce', 'Roumanie',
  'Maroc', 'Algérie', 'Tunisie', 'Sénégal', "Côte d'Ivoire", 'Cameroun', 'Mali',
  'Congo', 'Gabon', 'Madagascar', 'Maurice', 'États-Unis', 'Brésil', 'Chine', 'Japon',
];

// Return a readable text color (black/white) for a given background hex color
const getContrastText = (hex) => {
  if (!hex) return '#ffffff';
  let c = hex.replace('#', '');
  if (c.length === 3) c = c.split('').map((ch) => ch + ch).join('');
  if (c.length !== 6) return '#ffffff';
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  // Perceived luminance (YIQ)
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 150 ? '#111827' : '#ffffff';
};

const CfaAdminSettingsPage = ({ onMenuClick }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingStamp, setUploadingStamp] = useState(false);
  const fileInputRef = useRef(null);
  const stampInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    siret: '',
    nda: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'France',
    website: '',
    phone: '',
    email: '',
    pedagogical_manager_name: '',
    pedagogical_manager_email: '',
    pedagogical_manager_phone: '',
    logo_url: '',
    stamp_url: '',
    primary_color: '#7c3aed',
    subscription_status: 'trial'
  });

  useEffect(() => {
    fetchOrganizationSettings();
  }, []);

  // Apply primary color to CSS variable when it changes
  useEffect(() => {
    if (formData.primary_color) {
      document.documentElement.style.setProperty('--org-primary-color', formData.primary_color);
    }
  }, [formData.primary_color]);

  const fetchOrganizationSettings = async () => {
    setLoading(true);
    try {
      const response = await api.get('/organizations/current/settings');
      const data = response.data;
      setFormData({
        name: data.name || '',
        siret: data.siret || '',
        nda: data.nda || '',
        address: data.address || '',
        city: data.city || '',
        postal_code: data.postal_code || '',
        country: data.country || 'France',
        website: data.website || '',
        phone: data.phone || '',
        email: data.email || '',
        pedagogical_manager_name: data.pedagogical_manager_name || '',
        pedagogical_manager_email: data.pedagogical_manager_email || '',
        pedagogical_manager_phone: data.pedagogical_manager_phone || '',
        logo_url: data.logo_url || '',
        stamp_url: data.stamp_url || '',
        primary_color: data.primary_color || '#7c3aed',
        subscription_status: data.subscription_status || 'trial'
      });
    } catch (error) {
      console.error('Erreur chargement paramètres:', error);
      toast.error('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    const emptyToNull = (value) => {
      if (value === undefined || value === null) return null;
      const trimmed = String(value).trim();
      return trimmed === '' ? null : trimmed;
    };

    try {
      const updateData = {
        name: emptyToNull(formData.name) || formData.name,
        siret: emptyToNull(formData.siret),
        nda: emptyToNull(formData.nda),
        address: emptyToNull(formData.address),
        city: emptyToNull(formData.city),
        postal_code: emptyToNull(formData.postal_code),
        country: emptyToNull(formData.country) || 'France',
        website: emptyToNull(formData.website),
        phone: emptyToNull(formData.phone),
        email: emptyToNull(formData.email),
        pedagogical_manager_name: emptyToNull(formData.pedagogical_manager_name),
        pedagogical_manager_email: emptyToNull(formData.pedagogical_manager_email),
        pedagogical_manager_phone: emptyToNull(formData.pedagogical_manager_phone),
        primary_color: emptyToNull(formData.primary_color) || '#7c3aed',
      };

      if (!updateData.name) {
        toast.error('Le nom du centre est obligatoire');
        return;
      }

      // NDA must contain digits (déclaration d'activité is a numeric registration)
      if (updateData.nda && !/\d{6,}/.test(updateData.nda.replace(/\s/g, ''))) {
        toast.error("Le NDA doit être un numéro valide contenant des chiffres.");
        return;
      }

      // Email format (must include a top-level domain)
      const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
      if (updateData.email && !emailRe.test(updateData.email)) {
        toast.error("L'adresse email du centre n'est pas valide.");
        return;
      }
      if (updateData.pedagogical_manager_email && !emailRe.test(updateData.pedagogical_manager_email)) {
        toast.error("L'email du responsable pédagogique n'est pas valide.");
        return;
      }

      // Website must be a proper URL with a domain and TLD (rejects e.g. https://ss)
      if (updateData.website && !/^https?:\/\/([\w-]+\.)+[\w-]{2,}(\/.*)?$/i.test(updateData.website)) {
        toast.error("Le site web doit être une URL valide (ex. https://www.exemple.fr).");
        return;
      }
      
      await api.put('/organizations/current/settings', updateData);
      toast.success('Paramètres enregistrés avec succès');
      fetchOrganizationSettings();
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      const detail = error.response?.data?.detail;
      toast.error(
        typeof detail === 'string'
          ? detail
          : 'Erreur lors de la sauvegarde des paramètres'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non autorisé. Utilisez PNG, JPG, SVG ou WEBP');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Le fichier ne doit pas dépasser 5 Mo');
      return;
    }

    setUploadingLogo(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await api.post('/organizations/current/upload-logo', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setFormData(prev => ({ ...prev, logo_url: response.data.logo_url }));
      toast.success('Logo téléchargé avec succès');
    } catch (error) {
      console.error('Erreur upload logo:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du téléchargement du logo');
    } finally {
      setUploadingLogo(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteLogo = async () => {
    if (!formData.logo_url) return;
    
    try {
      await api.delete('/organizations/current/logo');
      setFormData(prev => ({ ...prev, logo_url: '' }));
      toast.success('Logo supprimé');
    } catch (error) {
      console.error('Erreur suppression logo:', error);
      toast.error('Erreur lors de la suppression du logo');
    }
  };

  // Stamp upload handlers
  const handleStampUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non autorisé. Utilisez PNG, JPG, SVG ou WEBP');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Le fichier ne doit pas dépasser 5 Mo');
      return;
    }

    setUploadingStamp(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await api.post('/organizations/current/upload-stamp', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setFormData(prev => ({ ...prev, stamp_url: response.data.stamp_url }));
      toast.success('Cachet téléchargé avec succès');
    } catch (error) {
      console.error('Erreur upload cachet:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du téléchargement du cachet');
    } finally {
      setUploadingStamp(false);
      if (stampInputRef.current) {
        stampInputRef.current.value = '';
      }
    }
  };

  const handleDeleteStamp = async () => {
    if (!formData.stamp_url) return;
    
    try {
      await api.delete('/organizations/current/stamp');
      setFormData(prev => ({ ...prev, stamp_url: '' }));
      toast.success('Cachet supprimé');
    } catch (error) {
      console.error('Erreur suppression cachet:', error);
      toast.error('Erreur lors de la suppression du cachet');
    }
  };

  const getSubscriptionBadge = (status) => {
    const badges = {
      active: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Actif' },
      trial: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Essai' },
      suspended: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Suspendu' },
      expired: { bg: 'bg-red-100', text: 'text-red-700', label: 'Expiré' }
    };
    const badge = badges[status] || badges.trial;
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const getLogoUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${process.env.REACT_APP_BACKEND_URL}${url.startsWith('/api') ? '' : '/api'}${url.startsWith('/') ? url : '/' + url}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="settings-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 overflow-x-hidden">
      <Header title="Paramètres du Centre" onMenuClick={onMenuClick} />
      
      <main className="max-w-4xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6 overflow-x-hidden">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              Paramètres du Centre
            </h2>
            <p className="text-slate-600 dark:text-slate-400">
              Ces informations apparaissent sur les documents officiels
            </p>
          </div>
          <Button 
            variant="outline" 
            onClick={fetchOrganizationSettings}
            disabled={loading}
            data-testid="refresh-settings-btn"
          >
            <RefreshCcw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>

        {/* Subscription Status Card */}
        <Card className="mb-6" data-testid="subscription-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">Statut de l'abonnement</CardTitle>
                <CardDescription>Votre plan actuel</CardDescription>
              </div>
              {getSubscriptionBadge(formData.subscription_status)}
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
              {formData.subscription_status === 'active' ? (
                <>
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span>Votre abonnement est actif. Accès complet à toutes les fonctionnalités.</span>
                </>
              ) : (
                <>
                  <AlertCircle className="w-4 h-4 text-amber-500" />
                  <span>Contactez le support pour mettre à jour votre abonnement.</span>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        <form onSubmit={handleSave}>
          {/* Organization Info */}
          <Card className="mb-6" data-testid="organization-info-card">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="w-5 h-5 text-violet-600" />
                Informations du Centre
              </CardTitle>
              <CardDescription>
                Informations administratives et légales
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Nom du centre *
                  </label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: Excellence Formation"
                    required
                    data-testid="org-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    SIRET
                  </label>
                  <Input
                    value={formData.siret}
                    onChange={(e) => setFormData({ ...formData, siret: e.target.value })}
                    placeholder="123 456 789 00012"
                    data-testid="org-siret-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    <FileText className="w-4 h-4 inline mr-1" />
                    Numéro de déclaration d'activité (NDA)
                  </label>
                  <Input
                    value={formData.nda}
                    onChange={(e) => setFormData({ ...formData, nda: e.target.value })}
                    placeholder="11999999999"
                    data-testid="org-nda-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    <Globe className="w-4 h-4 inline mr-1" />
                    Site web du centre
                  </label>
                  <Input
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    placeholder="https://www.moncentre.fr"
                    data-testid="org-website-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  <MapPin className="w-4 h-4 inline mr-1" />
                  Adresse
                </label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="12 rue de la Formation"
                  data-testid="org-address-input"
                />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Code postal
                  </label>
                  <Input
                    value={formData.postal_code}
                    onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                    placeholder="75001"
                    data-testid="org-postal-code-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Ville
                  </label>
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="Paris"
                    data-testid="org-city-input"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Pays
                  </label>
                  <Input
                    list="org-countries-list"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    placeholder="Rechercher un pays..."
                    data-testid="org-country-input"
                  />
                  <datalist id="org-countries-list">
                    {COUNTRIES.map((c) => <option key={c} value={c} />)}
                  </datalist>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact du centre */}
          <Card className="mb-6" data-testid="contact-card">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Phone className="w-5 h-5 text-violet-600" />
                Contact du Centre
              </CardTitle>
              <CardDescription>
                Coordonnées officielles du centre
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    <Phone className="w-4 h-4 inline mr-1" />
                    Téléphone
                  </label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="01 23 45 67 89"
                    data-testid="org-phone-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    <Mail className="w-4 h-4 inline mr-1" />
                    Email officiel
                  </label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="contact@moncentre.fr"
                    data-testid="org-email-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Responsable pédagogique */}
          <Card className="mb-6" data-testid="pedagogical-manager-card">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <User className="w-5 h-5 text-violet-600" />
                Responsable Pédagogique
              </CardTitle>
              <CardDescription>
                Personne référente pour les questions pédagogiques
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Nom complet
                </label>
                <Input
                  value={formData.pedagogical_manager_name}
                  onChange={(e) => setFormData({ ...formData, pedagogical_manager_name: e.target.value })}
                  placeholder="Marie Dupont"
                  data-testid="org-ped-manager-name-input"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    <Mail className="w-4 h-4 inline mr-1" />
                    Email
                  </label>
                  <Input
                    type="email"
                    value={formData.pedagogical_manager_email}
                    onChange={(e) => setFormData({ ...formData, pedagogical_manager_email: e.target.value })}
                    placeholder="responsable@moncentre.fr"
                    data-testid="org-ped-manager-email-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    <Phone className="w-4 h-4 inline mr-1" />
                    Téléphone
                  </label>
                  <Input
                    value={formData.pedagogical_manager_phone}
                    onChange={(e) => setFormData({ ...formData, pedagogical_manager_phone: e.target.value })}
                    placeholder="06 12 34 56 78"
                    data-testid="org-ped-manager-phone-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Identité Visuelle */}
          <Card className="mb-6" data-testid="branding-card">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Palette className="w-5 h-5 text-violet-600" />
                Identité Visuelle
              </CardTitle>
              <CardDescription>
                Logo et couleur principale pour vos documents
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Logo Upload */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                  <Image className="w-4 h-4 inline mr-1" />
                  Logo du centre
                </label>
                <div className="flex items-start gap-4">
                  {/* Logo Preview */}
                  <div className="w-32 h-32 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-600 flex items-center justify-center overflow-hidden bg-slate-50 dark:bg-slate-800">
                    {formData.logo_url ? (
                      <img 
                        src={getLogoUrl(formData.logo_url)} 
                        alt="Logo du centre" 
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => { 
                          e.target.style.display = 'none';
                          e.target.nextSibling?.classList?.remove('hidden');
                        }}
                      />
                    ) : (
                      <div className="text-center text-slate-400">
                        <Image className="w-10 h-10 mx-auto mb-1" />
                        <span className="text-xs">Aucun logo</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Upload Controls */}
                  <div className="flex-1 space-y-3">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                      onChange={handleLogoUpload}
                      className="hidden"
                      id="logo-upload"
                    />
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingLogo}
                        data-testid="upload-logo-btn"
                      >
                        {uploadingLogo ? (
                          <>
                            <RefreshCcw className="w-4 h-4 mr-2 animate-spin" />
                            Téléchargement...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            {formData.logo_url ? 'Changer le logo' : 'Télécharger un logo'}
                          </>
                        )}
                      </Button>
                      {formData.logo_url && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleDeleteLogo}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid="delete-logo-btn"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-slate-500">
                      Formats acceptés : PNG, JPG, SVG, WEBP. Taille max : 5 Mo.
                      <br />
                      Le logo apparaîtra sur tous les documents générés.
                    </p>
                  </div>
                </div>
              </div>

              {/* Stamp Upload */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                  <Stamp className="w-4 h-4 inline mr-1" />
                  Cachet officiel du centre
                </label>
                <div className="flex items-start gap-4">
                  {/* Stamp Preview */}
                  <div className="w-32 h-32 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-600 flex items-center justify-center overflow-hidden bg-slate-50 dark:bg-slate-800">
                    {formData.stamp_url ? (
                      <img 
                        src={getLogoUrl(formData.stamp_url)} 
                        alt="Cachet du centre" 
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => { 
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="text-center text-slate-400">
                        <Stamp className="w-10 h-10 mx-auto mb-1" />
                        <span className="text-xs">Aucun cachet</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Stamp Upload Controls */}
                  <div className="flex-1 space-y-3">
                    <input
                      ref={stampInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                      onChange={handleStampUpload}
                      className="hidden"
                      id="stamp-upload"
                    />
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => stampInputRef.current?.click()}
                        disabled={uploadingStamp}
                        data-testid="upload-stamp-btn"
                      >
                        {uploadingStamp ? (
                          <>
                            <RefreshCcw className="w-4 h-4 mr-2 animate-spin" />
                            Téléchargement...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            {formData.stamp_url ? 'Changer le cachet' : 'Télécharger un cachet'}
                          </>
                        )}
                      </Button>
                      {formData.stamp_url && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleDeleteStamp}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid="delete-stamp-btn"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-slate-500">
                      Formats acceptés : PNG, JPG, SVG, WEBP. Taille max : 5 Mo.
                      <br />
                      Le cachet sera apposé automatiquement sur les attestations, certificats et conventions.
                    </p>
                  </div>
                </div>
              </div>

              {/* Color Picker */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                  <Palette className="w-4 h-4 inline mr-1" />
                  Couleur principale
                </label>
                <div className="flex flex-wrap items-center gap-4">
                  <Input
                    type="color"
                    value={formData.primary_color}
                    onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                    className="w-16 h-12 p-1 cursor-pointer rounded-lg"
                    data-testid="org-color-picker"
                  />
                  <Input
                    type="text"
                    value={formData.primary_color}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^#[0-9A-Fa-f]{0,6}$/.test(value)) {
                        setFormData({ ...formData, primary_color: value });
                      }
                    }}
                    placeholder="#7c3aed"
                    className="w-28"
                    data-testid="org-color-input"
                  />
                  <div 
                    className="px-6 py-3 rounded-lg text-sm font-medium shadow-sm"
                    style={{ backgroundColor: formData.primary_color, color: getContrastText(formData.primary_color) }}
                  >
                    Aperçu
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Cette couleur sera utilisée pour les boutons et accents visuels dans les documents générés.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end gap-3 pb-8">
            <Button 
              type="button" 
              variant="outline" 
              onClick={fetchOrganizationSettings}
              data-testid="cancel-settings-btn"
            >
              Annuler
            </Button>
            <Button 
              type="submit" 
              disabled={saving} 
              data-testid="save-settings-btn"
              style={{ backgroundColor: formData.primary_color, color: getContrastText(formData.primary_color) }}
              className="hover:opacity-90"
            >
              {saving ? (
                <>
                  <RefreshCcw className="w-4 h-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer les modifications
                </>
              )}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default CfaAdminSettingsPage;
